import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import {
  getBookmarkedStrategies,
  unbookmarkStrategy,
} from "../services/strategyService";
import type { StrategyListItem } from "../services/strategyService";
import OptimizationConfigModal from "../components/optimizer/OptimizationConfigModal";
import { FEATURES } from "../featureFlags";

export function BookmarksPage() {
  const navigate = useNavigate();
  const [strategies, setStrategies] = useState<StrategyListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [optimizeStrategy, setOptimizeStrategy] = useState<{
    name: string;
    code: string;
    metrics?: {
      cagr?: number;
      sharpe?: number;
      max_drawdown?: number;
      sortino?: number;
    };
  } | null>(null);

  useEffect(() => {
    loadBookmarks();
  }, []);

  const loadBookmarks = async () => {
    try {
      const data = await getBookmarkedStrategies(100, 0);
      setStrategies(data.strategies || []);
    } catch (err) {
      console.error("Failed to load bookmarks:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveBookmark = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    try {
      await unbookmarkStrategy(id);
      setStrategies((prev) => prev.filter((s) => s.id !== id));
    } catch (err) {
      console.error("Failed to remove bookmark:", err);
    }
  };

  const handleStrategyClick = (strategy: StrategyListItem) => {
    navigate(`/backtest/${strategy.id}`);
  };

  return (
    <div>
      {/* Header with trading background */}
      <PageHeader
        title="Watchlist"
        subtitle="Your saved strategies for quick access"
        backgroundImage="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1920&q=80"
      />

      <div className="px-4 sm:px-6 lg:px-8 pb-8">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-4">
              <div className="spinner w-8 h-8" />
              <p className="text-surface-400">Loading watchlist...</p>
            </div>
          </div>
        ) : strategies.length === 0 ? (
          <div className="glass-card p-6 sm:p-12 text-center">
            <div className="w-20 h-20 mx-auto rounded-2xl bg-surface-800/50 flex items-center justify-center mb-6">
              <svg
                className="w-10 h-10 text-surface-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-surface-200 mb-3">
              No Strategies Yet
            </h2>
            <p className="text-surface-400 max-w-md mx-auto mb-6">
              Add strategies to your watchlist to keep track of your
              favorites.
            </p>
            <button
              onClick={() => navigate("/search")}
              className="btn btn-primary"
            >
              Community Strategies
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-surface-400">
                <span className="text-surface-200 font-medium">
                  {strategies.length}
                </span>{" "}
                strategies in your watchlist
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {strategies.map((strategy) => (
                <div
                  key={strategy.id}
                  onClick={() => handleStrategyClick(strategy)}
                  className="glass-card p-5 cursor-pointer hover:border-primary-500/50 transition-all group"
                >
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="text-lg font-semibold text-surface-100 group-hover:text-primary-400 transition-colors">
                      {strategy.name}
                    </h3>
                    <div className="flex items-center gap-1">
                      {FEATURES.OPTIMIZER && (
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            const { getStrategy } = await import("../services/strategyService");
                            const full = await getStrategy(strategy.id);
                            setOptimizeStrategy({
                              name: strategy.name,
                              code: full.code || "",
                              metrics: {
                                cagr: strategy.cagr,
                                sharpe: strategy.sharpe,
                                max_drawdown: strategy.max_drawdown,
                                sortino: strategy.sortino,
                              },
                            });
                          } catch (err) {
                            console.error("Failed to load strategy code for optimization:", err);
                          }
                        }}
                        className="p-1 text-primary-400 hover:text-primary-300 transition-colors"
                        title="Optimize this strategy"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                          />
                        </svg>
                      </button>
                      )}
                      <button
                        onClick={(e) => handleRemoveBookmark(e, strategy.id)}
                        className="p-1 text-surface-400 hover:text-red-400 transition-colors"
                        title="Remove from watchlist"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <p className="text-surface-400 text-sm mb-4 line-clamp-2">
                    {strategy.description || "No description available"}
                  </p>

                  {/* Metrics */}
                  <div className="grid grid-cols-3 gap-2 p-3 rounded-lg bg-surface-800/50">
                    <div className="text-center">
                      <div
                        className={`text-sm font-bold ${(strategy.cagr ?? 0) >= 0 ? "text-green-400" : "text-red-400"}`}
                      >
                        {(strategy.cagr ?? 0) >= 0 ? "+" : ""}
                        {(strategy.cagr ?? 0).toFixed(1)}%
                      </div>
                      <div className="text-xs text-surface-500">CAGR</div>
                    </div>
                    <div className="text-center border-x border-surface-700/50">
                      <div className="text-sm font-bold text-surface-200">
                        {(strategy.sharpe ?? 0).toFixed(2)}
                      </div>
                      <div className="text-xs text-surface-500">Sharpe</div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm font-bold text-red-400">
                        -{Math.abs(strategy.max_drawdown ?? 0).toFixed(1)}%
                      </div>
                      <div className="text-xs text-surface-500">Max DD</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Optimization Config Modal */}
      {optimizeStrategy && (
        <OptimizationConfigModal
          isOpen={!!optimizeStrategy}
          onClose={() => setOptimizeStrategy(null)}
          strategyCode={optimizeStrategy.code}
          strategyName={optimizeStrategy.name}
          currentMetrics={optimizeStrategy.metrics}
          onSubmit={(_jobId) => {
            setOptimizeStrategy(null);
            navigate("/optimizer/jobs?submitted=1");
          }}
        />
      )}
    </div>
  );
}
